import { useRouter } from 'next/router';

import { AppContextProps, useCart } from './useCart';
import { getCurrentStep, getNextStep } from '../utils/productFlow';
import { useCallback } from 'react';

const DEFAULT_STEPS = ['lead', 'main-payment', 'main-success'];
const DEFAULT_UPSELL_STEPS = ['upsell-payment', 'upsell-success'];

export const useProductFlow = () => {
  const context = useCart() as AppContextProps;
  const router = useRouter();

  const productFlow = context.productFlow;

  const currentStep = getCurrentStep({
    productFlow: productFlow,
    pathname: router.pathname,
  });

  const nextStep = getNextStep({
    productFlow: productFlow,
    pathname: router.pathname,
  });

  const getStepsCount = useCallback(
    ({
      isUpsellFlow,
      isUpgradeFlow,
      isRecoveryFlow,
      hasMultiProfileFlow,
    }: {
      isUpsellFlow?: boolean;
      isUpgradeFlow?: boolean;
      isRecoveryFlow?: boolean;
      hasMultiProfileFlow?: boolean;
    }) => {
      if (!productFlow || !productFlow?.steps) {
        return {
          totalStepsCount: 0,
          currentStepCount: 0,
        };
      }

      let flowType: 'recovery' | 'upgrade' | 'upsell' | 'default' = 'default';
      if (isRecoveryFlow) flowType = 'recovery';
      else if (isUpgradeFlow) flowType = 'upgrade';
      else if (isUpsellFlow) flowType = 'upsell';

      const allSteps = productFlow.steps.filter(({ id }) => {
        if (flowType === 'default') {
          return !/(upsell|upgrade|recovery)/.test(id);
        }
        if (flowType === 'upsell')
          return hasMultiProfileFlow
            ? id.includes('upsell')
            : id.includes('upsell') && !id.includes('multiprofile');
        return id.includes(flowType);
      });

      const currentStepIndex = allSteps.findIndex(
        (step) => step.id === currentStep.id
      );

      const defaultStepsCount = DEFAULT_STEPS.length;
      const upsellStepsCount = DEFAULT_UPSELL_STEPS.length;
      const totalStepsCount = Math.max(
        allSteps.length,
        isUpsellFlow || isUpgradeFlow ? upsellStepsCount : defaultStepsCount
      );
      const currentStepCount = currentStepIndex + 1;

      return {
        totalStepsCount,
        currentStepCount,
      };
    },
    [productFlow?.steps, currentStep.id]
  );

  return { currentStep, nextStep, productFlow, getStepsCount };
};
